import React, { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Circle, Polygon, Popup, useMap, Marker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './ZoneMap.css';
import type { Zone } from '../services/zoneApi';
import type { CurrentLocation } from '../hooks/useCurrentLocation';
import { MapPin, Navigation } from 'lucide-react';

// Fix para ícones padrão do Leaflet
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import iconRetina from 'leaflet/dist/images/marker-icon-2x.png';
import { useI18n } from '../contexts/I18nContext';

const DefaultIcon = L.icon({
  iconUrl: icon,
  iconRetinaUrl: iconRetina,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

interface ZoneMapProps {
  zones: Zone[];
  selectedZone?: Zone | null;
  onZoneClick?: (zone: Zone) => void;
  height?: string;
  showProperties?: boolean;
  properties?: Array<{
    id: string;
    name: string;
    latitude?: number;
    longitude?: number;
  }>;
  currentLocation?: CurrentLocation | null;
  showCurrentLocation?: boolean;
}

// Componente para ajustar o mapa quando zonas mudam
function MapUpdater({
  zones,
  selectedZone,
  currentLocation,
}: {
  zones: Zone[];
  selectedZone?: Zone | null;
  currentLocation?: CurrentLocation | null;
}) {
  const map = useMap();

  useEffect(() => {
    if (selectedZone) {
      map.setView([selectedZone.center_lat, selectedZone.center_lon], selectedZone.radius ? 12 : 11);
      return;
    }

    const activeZones = zones.filter(z => z.is_active);
    const bounds: [number, number][] = activeZones.map(
      zone => [zone.center_lat, zone.center_lon] as [number, number]
    );

    if (currentLocation) {
      bounds.push([currentLocation.lat, currentLocation.lon]);
    }

    if (bounds.length > 1) {
      map.fitBounds(bounds, { padding: [50, 50] });
      return;
    }

    if (bounds.length === 1) {
      map.setView(bounds[0], 13);
    }
  }, [zones, selectedZone, currentLocation, map]);

  return null;
}

function MapResize({ deps }: { deps: unknown[] }) {
  const map = useMap();

  useEffect(() => {
    const timer = window.setTimeout(() => {
      map.invalidateSize();
    }, 150);

    return () => window.clearTimeout(timer);
  }, [map, ...deps]);

  return null;
}

const CurrentLocationIcon = L.divIcon({
  className: 'current-location-marker',
  html: '<div style="width:16px;height:16px;background:#2563eb;border:3px solid white;border-radius:50%;box-shadow:0 0 0 2px #2563eb,0 2px 6px rgba(0,0,0,0.3);"></div>',
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

const ZoneMap: React.FC<ZoneMapProps> = ({
  zones,
  selectedZone,
  onZoneClick,
  height = '500px',
  showProperties = false,
  properties = [],
  currentLocation = null,
  showCurrentLocation = true,
}) => {
  const { t } = useI18n();
  const mapRef = useRef<L.Map>(null);

  // Cores para diferentes zonas
  const zoneColors = [
    '#3b82f6', // blue
    '#10b981', // green
    '#f59e0b', // amber
    '#ef4444', // red
    '#8b5cf6', // purple
    '#ec4899', // pink
    '#06b6d4', // cyan
    '#84cc16', // lime
  ];

  const getZoneColor = (index: number) => zoneColors[index % zoneColors.length];

  // Parse GeoJSON boundaries
  const parseBoundaries = (boundaries: string | undefined): [number, number][] | null => {
    if (!boundaries) return null;
    try {
      const geoJson = JSON.parse(boundaries);
      if (geoJson.type === 'Polygon' && geoJson.coordinates && geoJson.coordinates[0]) {
        // GeoJSON usa [lon, lat], Leaflet usa [lat, lon]
        return geoJson.coordinates[0].map((coord: number[]) => [coord[1], coord[0]]);
      }
    } catch (e) {
      console.error('Erro ao parsear boundaries:', e);
    }
    return null;
  };

  // Centro padrão (São Paulo)
  const defaultCenter: [number, number] = [-23.550520, -46.633308];
  const defaultZoom = 11;

  const activeZones = zones.filter(z => z.is_active);

  return (
    <div className="zone-map-wrapper" style={{ height, minHeight: height }}>
      {showCurrentLocation && currentLocation && (
        <button
          type="button"
          onClick={() => mapRef.current?.setView([currentLocation.lat, currentLocation.lon], 14)}
          className="zone-map-locate-btn"
          title={t('zone.centerOnMe')}
        >
          <Navigation size={16} color="#2563eb" />
          Minha localização
        </button>
      )}
      <MapContainer
        center={defaultCenter}
        zoom={defaultZoom}
        style={{ height: '100%', width: '100%', minHeight: height }}
        ref={mapRef}
      >
        <MapResize deps={[height, currentLocation, zones.length, selectedZone?.id]} />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <MapUpdater
          zones={zones}
          selectedZone={selectedZone}
          currentLocation={showCurrentLocation ? currentLocation : null}
        />

        {showCurrentLocation && currentLocation && (
          <>
            {currentLocation.source === 'gps' && currentLocation.accuracy && (
              <Circle
                center={[currentLocation.lat, currentLocation.lon]}
                radius={currentLocation.accuracy}
                pathOptions={{
                  color: '#2563eb',
                  fillColor: '#2563eb',
                  fillOpacity: 0.1,
                  weight: 1,
                  dashArray: '4 4',
                }}
              />
            )}
            <Marker
              position={[currentLocation.lat, currentLocation.lon]}
              icon={CurrentLocationIcon}
            >
              <Popup>
                <div style={{ minWidth: '160px' }}>
                  <h4 style={{ margin: '0 0 6px 0', fontWeight: 'bold' }}>{t('zone.yourLocation')}</h4>
                  <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#666' }}>
                    {currentLocation.city
                      ? `${currentLocation.city}${currentLocation.region ? `, ${currentLocation.region}` : ''}`
                      : `${currentLocation.lat.toFixed(6)}, ${currentLocation.lon.toFixed(6)}`}
                  </p>
                  <p style={{ margin: 0, fontSize: '11px', color: '#888' }}>
                    {currentLocation.source === 'gps'
                      ? `GPS${currentLocation.accuracy ? ` (±${Math.round(currentLocation.accuracy)}m)` : ''}`
                      : 'Localização aproximada (IP)'}
                  </p>
                </div>
              </Popup>
            </Marker>
          </>
        )}

        {/* Renderizar zonas */}
        {activeZones.map((zone, index) => {
          const color = selectedZone?.id === zone.id ? '#ef4444' : getZoneColor(index);
          const opacity = selectedZone?.id === zone.id ? 0.4 : 0.2;
          const weight = selectedZone?.id === zone.id ? 3 : 2;

          // Se tem raio, usar círculo
          if (zone.radius && zone.radius > 0) {
            return (
              <Circle
                key={zone.id}
                center={[zone.center_lat, zone.center_lon]}
                radius={zone.radius * 1000} // Converter km para metros
                pathOptions={{
                  color,
                  fillColor: color,
                  fillOpacity: opacity,
                  weight,
                }}
                eventHandlers={{
                  click: () => onZoneClick?.(zone),
                }}
              >
                <Popup>
                  <div style={{ minWidth: '150px' }}>
                    <h3 style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>{zone.name}</h3>
                    {zone.description && (
                      <p style={{ margin: '0 0 4px 0', fontSize: '14px', color: '#666' }}>
                        {zone.description}
                      </p>
                    )}
                    <p style={{ margin: '4px 0', fontSize: '12px', color: '#888' }}>
                      Raio: {zone.radius} km
                    </p>
                    <p style={{ margin: '4px 0', fontSize: '12px', color: '#888' }}>
                      Centro: {zone.center_lat.toFixed(6)}, {zone.center_lon.toFixed(6)}
                    </p>
                  </div>
                </Popup>
              </Circle>
            );
          }

          // Se tem boundaries (polígono), usar polígono
          const polygonCoords = parseBoundaries(zone.boundaries);
          if (polygonCoords && polygonCoords.length > 0) {
            return (
              <Polygon
                key={zone.id}
                positions={polygonCoords}
                pathOptions={{
                  color,
                  fillColor: color,
                  fillOpacity: opacity,
                  weight,
                }}
                eventHandlers={{
                  click: () => onZoneClick?.(zone),
                }}
              >
                <Popup>
                  <div style={{ minWidth: '150px' }}>
                    <h3 style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>{zone.name}</h3>
                    {zone.description && (
                      <p style={{ margin: '0 0 4px 0', fontSize: '14px', color: '#666' }}>
                        {zone.description}
                      </p>
                    )}
                    <p style={{ margin: '4px 0', fontSize: '12px', color: '#888' }}>
                      Centro: {zone.center_lat.toFixed(6)}, {zone.center_lon.toFixed(6)}
                    </p>
                  </div>
                </Popup>
              </Polygon>
            );
          }

          // Fallback: apenas marcador no centro
          return (
            <Marker
              key={zone.id}
              position={[zone.center_lat, zone.center_lon]}
              eventHandlers={{
                click: () => onZoneClick?.(zone),
              }}
            >
              <Popup>
                <div style={{ minWidth: '150px' }}>
                  <h3 style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>{zone.name}</h3>
                  {zone.description && (
                    <p style={{ margin: '0 0 4px 0', fontSize: '14px', color: '#666' }}>
                      {zone.description}
                    </p>
                  )}
                  <p style={{ margin: '4px 0', fontSize: '12px', color: '#888' }}>
                    Centro: {zone.center_lat.toFixed(6)}, {zone.center_lon.toFixed(6)}
                  </p>
                </div>
              </Popup>
            </Marker>
          );
        })}

        {/* Renderizar propriedades se solicitado */}
        {showProperties &&
          properties
            .filter(p => p.latitude && p.longitude)
            .map(property => (
              <Marker
                key={property.id}
                position={[property.latitude!, property.longitude!]}
              >
                <Popup>
                  <div style={{ minWidth: '150px' }}>
                    <h4 style={{ margin: '0 0 4px 0', fontWeight: 'bold' }}>{property.name}</h4>
                    <p style={{ margin: '0', fontSize: '12px', color: '#888' }}>
                      <MapPin size={12} style={{ display: 'inline', marginRight: '4px' }} />
                      {property.latitude?.toFixed(6)}, {property.longitude?.toFixed(6)}
                    </p>
                  </div>
                </Popup>
              </Marker>
            ))}
      </MapContainer>
    </div>
  );
};

export default ZoneMap;

